import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  confirmPublicAppointment,
  fetchPublicAppointment,
} from "../../../api/service/appointmentConfirm";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";

const PublicSection = ({ title, children }) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="border-b border-slate-100 px-6 py-4">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
    </div>
    <div className="px-6 py-5">{children}</div>
  </section>
);

function stripHtmlIsMeaningful(html) {
  if (typeof html !== "string" || !html.trim()) return false;
  const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return text.length > 0;
}

function appointmentDateLabel(raw, locale) {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(locale, { dateStyle: "long" });
}

export const PublicAppointmentConfirm = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const dateLocale =
    i18n.language?.startsWith("pl") ? "pl-PL" : i18n.language?.startsWith("no") ? "nb-NO" : i18n.language?.startsWith("sv") ? "sv-SE" : undefined;
  const [state, setState] = useState({
    phase: "loading",
    errorMessage: null,
    data: null,
    submitError: null,
    done: false,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) {
        setState((s) => ({ ...s, phase: "error", errorMessage: t("publicAppointment.missingToken") }));
        return;
      }
      try {
        const res = await fetchPublicAppointment(token);
        if (cancelled) return;
        setState((s) => ({
          ...s,
          phase: "ready",
          data: res?.data?.data || null,
          errorMessage: null,
        }));
      } catch (e) {
        if (cancelled) return;
        const msg =
          e?.response?.data?.errors?.[0] ||
          e?.response?.data?.error ||
          t("publicAppointment.loadFailed");
        setState((s) => ({ ...s, phase: "error", errorMessage: msg }));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const onConfirm = async () => {
    setState((s) => ({ ...s, phase: "submitting", submitError: null }));
    try {
      const res = await confirmPublicAppointment(token);
      const messages = res?.data?.messages || [];
      const errors = res?.data?.errors || [];
      if (errors.length && !messages.length) {
        setState((s) => ({
          ...s,
          phase: "ready",
          submitError: errors.join(", "),
        }));
        return;
      }
      setState((s) => ({
        ...s,
        phase: "done",
        done: true,
        submitError: null,
      }));
    } catch (e) {
      const msg =
        e?.response?.data?.errors?.[0] ||
        e?.response?.data?.messages?.[0] ||
        t("publicAppointment.confirmFailed");
      setState((s) => ({
        ...s,
        phase: "ready",
        submitError: msg,
      }));
    }
  };

  if (state.phase === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 py-16">
        <ResourceLoading />
      </div>
    );
  }

  if (state.phase === "error") {
    return (
      <div className="min-h-screen bg-slate-50 py-16 px-4">
        <div className="mx-auto max-w-lg">
          <ResourceError errors={state.errorMessage || t("publicAppointment.loadFailed")} />
        </div>
      </div>
    );
  }

  const d = state.data || {};
  const appointment = d.appointment || {};
  const salesConditionsUrl =
    typeof d.sales_conditions_url === "string" && d.sales_conditions_url.trim().length > 0
      ? d.sales_conditions_url.trim()
      : null;
  const reportTypeTitle = d.report_type?.title || null;
  const addressLine =
    typeof d.address_summary === "string" && d.address_summary.trim().length > 0
      ? d.address_summary.trim()
      : null;
  const hasDescription = stripHtmlIsMeaningful(d.description);
  const showWorkSection = Boolean(reportTypeTitle || addressLine || hasDescription);
  const appointmentDateDisplay = appointmentDateLabel(appointment.date, dateLocale) || "—";

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:py-16">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="text-center">
          <p className="text-sm font-medium uppercase tracking-wide text-sky-600">
            {d.company_name || t("publicAppointment.companyFallback")}
          </p>
          <h1 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            {t("publicAppointment.title")}
          </h1>
          <p className="mt-2 text-slate-600">{t("publicAppointment.subtitle")}</p>
        </div>

        <PublicSection title={t("publicAppointment.sectionAppointment")}>
          <dl className="grid grid-cols-1 gap-3 text-sm text-slate-700 sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">{t("publicAppointment.reportNumber")}</dt>
              <dd className="font-medium text-slate-900">{d.report_number || "—"}</dd>
            </div>
            <div>
              <dt className="text-slate-500">{t("publicAppointment.date")}</dt>
              <dd className="font-medium text-slate-900">{appointmentDateDisplay}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-slate-500">{t("publicAppointment.time")}</dt>
              <dd className="font-medium text-slate-900">
                {appointment.time ? (
                  <>
                    <span className="block">{appointment.time}</span>
                    <span className="mt-1 block text-xs font-normal italic text-slate-500">
                      {t("publicAppointment.timeExactHint")}
                    </span>
                  </>
                ) : appointment.time_from && appointment.time_too ? (
                  <>
                    <span className="block">
                      {appointment.time_from} – {appointment.time_too}
                    </span>
                    <span className="mt-1 block text-xs font-normal italic text-slate-500">
                      {t("publicAppointment.timeWindowHint")}
                    </span>
                  </>
                ) : (
                  "—"
                )}
              </dd>
            </div>
          </dl>
        </PublicSection>

        {showWorkSection ? (
          <PublicSection title={t("publicAppointment.sectionWork")}>
            <dl className="grid grid-cols-1 gap-4 text-sm text-slate-700">
              {reportTypeTitle ? (
                <div>
                  <dt className="text-slate-500">{t("publicAppointment.reportType")}</dt>
                  <dd className="font-medium text-slate-900">{reportTypeTitle}</dd>
                </div>
              ) : null}
              {addressLine ? (
                <div>
                  <dt className="text-slate-500">{t("publicAppointment.serviceAddress")}</dt>
                  <dd className="font-medium text-slate-900">{addressLine}</dd>
                </div>
              ) : null}
              {hasDescription ? (
                <div>
                  <dt className="mb-1 text-slate-500">{t("publicAppointment.workDescription")}</dt>
                  <dd
                    className="prose prose-sm max-w-none text-slate-800 prose-p:my-2 prose-ul:my-2"
                    dangerouslySetInnerHTML={{ __html: d.description }}
                  />
                </div>
              ) : null}
            </dl>
          </PublicSection>
        ) : null}

        {state.done ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-5 text-center text-emerald-900">
            <p className="text-lg font-semibold">{t("publicAppointment.successTitle")}</p>
            <p className="mt-2 text-sm">{t("publicAppointment.successBody")}</p>
          </div>
        ) : (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            {appointment.approved ? (
              <p className="text-center text-slate-700">{t("publicAppointment.alreadyConfirmed")}</p>
            ) : (
              <>
                {state.submitError ? (
                  <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">{state.submitError}</p>
                ) : null}
                <p className="text-center text-sm italic text-slate-600">
                  {salesConditionsUrl ? (
                    <>
                      {t("publicAppointment.termsAcceptPrefix")}
                      <a
                        href={salesConditionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium not-italic text-sky-600 underline decoration-sky-600/30 underline-offset-2 hover:text-sky-700"
                      >
                        {t("publicAppointment.termsAcceptLink")}
                      </a>
                      {t("publicAppointment.termsAcceptSuffix")}
                    </>
                  ) : (
                    t("publicAppointment.termsAcceptPlain")
                  )}
                </p>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={state.phase === "submitting"}
                    className="inline-flex min-w-[12rem] items-center justify-center rounded-xl bg-sky-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {state.phase === "submitting"
                      ? t("publicAppointment.submitting")
                      : t("publicAppointment.confirmButton")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
